import { Period } from '../../types/fiscalYear/FiscalYear'
import { BalanceRegistry } from '../financialStatements'
import { VariableType } from '../../components/Table/types'

/**
 * Jakotavat aggregoidun budjetin jakamiseen kuukausille
 */
export enum AllocationMethod {
  /** Tasajako: summa / kuukausien määrä */
  DIVIDE_EVENLY = 'DIVIDE_EVENLY',
  /** Sama summa jokaiseen kuukauteen */
  ADD_TO_EVERY_MONTH = 'ADD_TO_EVERY_MONTH',
  /** Historiapainotus: valitulla aikavälillä + valinnainen kasvuprosentti */
  WEIGHTED = 'WEIGHTED'
}

/**
 * Yksittäisen kuukauden historiadata painotusta varten
 */
export interface HistoryDataPoint {
  period: Period
  actuals: number | null
  budget: number | null
}

/**
 * Jakofunktion optiot
 */
export interface AllocationOptions {
  /** Historiajakso painotusta varten (PAKOLLINEN jos WEIGHTED) */
  historyRange?: {
    startPeriod: Period
    endPeriod: Period
  }
  /** Kasvuprosentti (esim. 0.05 = 5%, valinnainen, oletus 0) */
  growthPercentage?: number
  /** Historiadata painotusta varten */
  historyData?: HistoryDataPoint[]
  /** @deprecated Ei enää käytössä - arvot tallennetaan täydellä tarkkuudella, pyöristys tapahtuu vain UI:ssa */
  decimals?: number
}

/**
 * Yksittäinen jaettu kuukausiarvo
 * Käytännössä AccountBudgetData ilman account- ja dimension-kontekstia
 */
export interface MonthlyBudget {
  year: number
  month: number
  value: number | null
}

/**
 * Jakofunktion signature
 */
export type AllocationFunction = (
  totalSum: number,
  targetPeriods: Period[],
  options?: AllocationOptions
) => MonthlyBudget[]

/**
 * Entity type for allocation rows
 */
export type AllocationEntityType = 'account' | 'formula'

/**
 * Jako-rivi modalissa - yksi aggregoitu solu per rivi
 *
 * Luodaan "flatten" operaatiolla: EnhancedStatementRow → AllocationBudgetRow[]
 * Jokainen newBudgets.groups entry tuottaa oman rivin
 */
export interface AllocationBudgetRow {
  /** Uniiikki tunniste riville: "accountCode_dimensionId_dataKey" */
  id: string

  // Entity context
  /** Entity type (account or formula) - determines converters and UI behavior */
  entityType: AllocationEntityType

  // Tilikonteksti (EnhancedStatementRow:sta)
  /** Tilikoodi (or formulaId for formula entities) */
  accountCode: number
  /** Tilin nimi (or formula name for formula entities) */
  accountName: string
  /** Dimension ID (null jos account-tason budjetti) */
  dimensionId: string | null
  /** Dimension nimi näyttöä varten */
  dimensionName?: string
  /** Toteuma historia dataa varten */
  actuals?: BalanceRegistry
  /** Data type (budget, actuals, forecast) */
  dataType: string
  /** Variable type for display and editing: 'absolute', 'percentage' (0.5 = 50%), or 'ratio' */
  variableType: VariableType
  /** Style configuration (e.g., decimals) */
  style?: {
    /** Number of decimal places for formatting (default: 2) */
    decimals?: number
  }

  // Aggregoitu solu (yksi groups-entry)
  /** DataKey groups-mapista (column.dataKey) */
  dataKey: number
  /** PeriodGroup tai FiscalYear objekti */
  periodGroup: any // FiscalYear | PeriodGroup
  /** Kohdejakso periodit */
  targetPeriods: Period[]
  /** Jakson kuvaus näyttöä varten (esim. "FY2025", "Q1 2025") */
  periodLabel: string
  /** Jakson pituus kuukausina */
  periodLength: number
  /** Muokattu aggregoitu arvo */
  value: number
  /** Jos true, merkitsee budjettien poistoa (value oli null) */
  isDeletion?: boolean

  // Jakotapa
  /** Valittu jakotapa */
  allocationMethod: AllocationMethod
  /** Jakotavan optiot (kasvuprosentti, historiadata jne) */
  allocationOptions?: AllocationOptions
}

/**
 * BudgetSaveModal props
 */
export interface BudgetAllocationModalProps {
  open: boolean
  onClose: () => void
  onSave: (budgetData: any) => Promise<void>
  /** Grid API to read modified rows */
  gridApi?: any // GridApi from ag-grid
  /** Grid columns (needed to map dataKey → periodGroup) */
  columns: any[] // StatementColumn[] from financialStatements feature
  /**
   * Converter function to transform grid rows to AllocationBudgetRow[].
   * Selected automatically based on entityType in useBudgetAllocation hook.
   */
  convertToAllocationRows: (rowData: any[], columns: any[]) => AllocationBudgetRow[]
  /**
   * Converter function to transform MonthlyBudget[] to budget data format.
   * Selected automatically based on entityType in useBudgetAllocation hook.
   *
   * @param monthlyBudgets - Allocated monthly budgets (year, month, value)
   * @param row - Source allocation row (contains accountCode/formulaId, dimensionId, etc.)
   * @returns Array of budget data entries for this row
   */
  convertToBudgetRows: (monthlyBudgets: MonthlyBudget[], row: AllocationBudgetRow) => any[]
}

/**
 * Jakotuloksen validointi
 */
export interface AllocationValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}
